// src/components/DateRangePicker.tsx
import React, { useState } from "react";
import { CalendarIcon } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Calendar } from "../../components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover";
import { format } from "date-fns";
import { DateRange } from "../../types/tradingJournal";
import { cn } from "../../lib/utils";
import { useIsMobile } from "../../hooks/use-mobile";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";

interface DateRangePickerProps {
  dateRange: DateRange;
  onDateRangeChange: (range: DateRange) => void;
  disabled?: boolean;
}

export default function DateRangePicker({
  dateRange,
  onDateRangeChange,
  disabled,
}: DateRangePickerProps) {
  const [open, setOpen] = useState(false);
  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = useState<"range" | "start" | "end">(
    "range"
  );

  const [tempStartDate, setTempStartDate] = useState<Date | undefined>(
    dateRange.startDate
  );
  const [tempEndDate, setTempEndDate] = useState<Date | undefined>(
    dateRange.endDate
  );

  const handleRangeSelect = (range: { from: Date; to?: Date }) => {
    if (range.from && range.to) {
      onDateRangeChange({
        startDate: range.from,
        endDate: range.to,
      });
      setTempStartDate(range.from);
      setTempEndDate(range.to);
      setOpen(false);
    }
  };

  const handleStartDateSelect = (date: Date | undefined) => {
    if (date) {
      setTempStartDate(date);
      if (tempEndDate && date > tempEndDate) {
        setTempEndDate(date);
      }
      onDateRangeChange({
        startDate: date,
        endDate: tempEndDate || date,
      });
    }
  };

  const handleEndDateSelect = (date: Date | undefined) => {
    if (date) {
      setTempEndDate(date);
      if (tempStartDate && date < tempStartDate) {
        setTempStartDate(date);
      }
      onDateRangeChange({
        startDate: tempStartDate || date,
        endDate: date,
      });
    }
  };

  const handleApply = () => {
    if (tempStartDate && tempEndDate) {
      onDateRangeChange({
        startDate: tempStartDate,
        endDate: tempEndDate,
      });
    }
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          disabled={disabled}
          className={cn(
            "bg-white/5 border-white/10 hover:bg-white/10 justify-start text-left w-full",
            "text-white",
            disabled && "opacity-50 cursor-not-allowed",
            isMobile && "text-sm h-auto py-2"
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4 text-white" />
          {dateRange.startDate && dateRange.endDate ? (
            <span className={cn(isMobile && "text-xs")}>
              {format(dateRange.startDate, "MMM d, yyyy")} –{" "}
              {format(dateRange.endDate, "MMM d, yyyy")}
            </span>
          ) : (
            <span>Select date range</span>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        className="w-auto p-3 bg-darkMode-layer2 text-white border border-white/10 shadow-lg"
      >
        <Tabs
          value={activeTab}
          onValueChange={(v) => setActiveTab(v as any)}
          className="w-full"
        >
          <TabsList className="grid grid-cols-3 mb-4 bg-transparent border-b border-white/20">
            {["range", "start", "end"].map((tab) => (
              <TabsTrigger
                key={tab}
                value={tab}
                className={cn(
                  "data-[state=inactive]:text-white/50",
                  "data-[state=active]:bg-white/10 data-[state=active]:text-market-teal data-[state=active]:rounded-md data-[state=active]:p-2",
                  "rounded-none"
                )}
              >
                {tab === "range"
                  ? "Range"
                  : tab === "start"
                  ? "Start Date"
                  : "End Date"}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="range" className="mt-0 bg-transparent p-0">
            <Calendar
              mode="range"
              selected={{
                from: tempStartDate || dateRange.startDate,
                to: tempEndDate || dateRange.endDate,
              }}
              onSelect={handleRangeSelect}
              initialFocus
              defaultMonth={tempStartDate || dateRange.startDate || new Date()}
              numberOfMonths={isMobile ? 1 : 2}
              className="p-3 pointer-events-auto bg-transparent text-white"
            />
          </TabsContent>

          <TabsContent value="start" className="mt-0 bg-transparent p-0">
            <Calendar
              mode="single"
              selected={tempStartDate}
              onSelect={handleStartDateSelect}
              initialFocus
              defaultMonth={tempStartDate || new Date()}
              numberOfMonths={1}
              className="p-3 pointer-events-auto bg-transparent text-white"
            />
            <div className="text-sm text-white/70 mt-2">
              Selected:{" "}
              {tempStartDate ? format(tempStartDate, "MMM d, yyyy") : "None"}
            </div>
          </TabsContent>

          <TabsContent value="end" className="mt-0 bg-transparent p-0">
            <Calendar
              mode="single"
              selected={tempEndDate}
              onSelect={handleEndDateSelect}
              initialFocus
              defaultMonth={tempEndDate || new Date()}
              numberOfMonths={1}
              className="p-3 pointer-events-auto bg-transparent text-white"
            />
            <div className="text-sm text-white/70 mt-2">
              Selected:{" "}
              {tempEndDate ? format(tempEndDate, "MMM d, yyyy") : "None"}
            </div>
          </TabsContent>
        </Tabs>

        <div className="flex justify-end mt-4">
          <Button
            onClick={handleApply}
            className="intellectia-button text-black "
          >
            Apply
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
